import React, { useEffect, useRef, useState } from 'react';
import { AppHeader } from '../components/app-header';
import { AppFooter } from '../components/app-footer';
import { EventNotificationService, useEventNotification } from '../framework/services/event-notification';
import { AppPages } from './app-pages';
import { BrowserRouter as Router } from 'react-router-dom';
import { getSinglePageAppStyles } from '../app-core/app-styles';
import { mergeStyles } from '@fluentui/react';
import { BwjSemiFlexContainer } from '../framework/components/bwj-semi-flex-container';
import { BwjScrollContainer } from '../framework/components/bwj-scroll-container';
import { BwjViewManager, BwjViewState } from '../framework/components/bwj-view-manager';
import { BwjButtonType } from '../framework/components/bwj-button';
import { BrowseAppEvents } from './events';
import { BwjEventArgs } from '../framework/entities/bwj-event';
import { BwjToaster } from '../framework/components/bwj-toaster';

const styles = getSinglePageAppStyles();

const subscriberId = 'Component.AppContent';
const resetMainContentScrollEvent = `${subscriberId}.ResetMainContentScroll`

export const AppContent: React.FunctionComponent = () => {
  const [showViews, setShowViews] = useState(false);
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const loadingMessage = useRef('Loading application...');
  const [scrollResetInvocation, setScrollResetInvocation] = useState<BwjEventArgs<null> | undefined>(undefined);

  const showLoadingView = (message: string) => {
    loadingMessage.current = message;
    setViewState(BwjViewState.Loading);
  }

  useEventNotification(() => setScrollResetInvocation(new BwjEventArgs(null)), [], resetMainContentScrollEvent, subscriberId);
  useEventNotification((msg) => showLoadingView(msg), [], BrowseAppEvents.ShowAppLoadingWithMessage, subscriberId);
  useEventNotification(() => setViewState(BwjViewState.Ready), [], BrowseAppEvents.ShowAppReady, subscriberId);
  
  useEffect(() => {
    // only ever do this once.  We do this to ensure that no app view can fire a Menu Available / Unavailable event
    // before this parent component has subscribed to it
    if(!showViews) {
      window.setTimeout(() => {
        setShowViews(true);
        setViewState(BwjViewState.Ready);
      }, 100);
    }
  });

  const appBodyClass = mergeStyles([styles.root]);
  // const mainSectionClass = mergeStyles([styles.main]);
  const contentAreaClass = mergeStyles([styles.contentArea]);

  const onRestartApp = () => {
    window.location.reload();
  }

  return (
    <Router>
      <div className={appBodyClass}>
        <AppHeader
          showMenu={false}
          showCart={false}
          logoHref={'https://bwjstorm.com'}
          showCustomerAccount={false}
          onDesktopMenuButtonClick={() => {}}
          onMobileMenuButtonClick={() => {}}
        />
        <BwjSemiFlexContainer
          styles={{ root: { flexGrow: 1 }, content: { display: 'flex', flexDirection: 'row', alignItems: 'stretch' } }}
          pinWidth={false}
          pinHeight={true}
        >
          <section className={contentAreaClass}>
            <BwjViewManager
              state={viewState}
              loadingState={{
                message: loadingMessage.current,
              }}
              errorState={{
                icon: 'StatusErrorFull',
                message: 'We could not start the app due to an error - try restarting the app.  Please be sure to check your Internet connection',
                buttons: [{
                  buttonStyle: BwjButtonType.PrimaryStandard,
                  text: 'Restart app',
                  onClick: onRestartApp,
                }],
              }}
            >
              <BwjScrollContainer
                containerStyle={{ display: 'flex', flexDirection: 'column' }}
                invokeScrollReset={scrollResetInvocation}
              >
                <AppPages />
              </BwjScrollContainer>
            </BwjViewManager>
          </section>
        </BwjSemiFlexContainer>
        <AppFooter />
      </div>
      <BwjToaster
        ariaShowFullMessageLabel={'Display full notification'}
        ariaDismissLabel={'Dismiss notification'}
        closeDialogButtonText={'Close'}
      />
    </Router>
  );
};

export const useMainContentScrollReset = () => {
  const fn = () => EventNotificationService.dispatch(resetMainContentScrollEvent);
  return fn;
}